import type { SliderFieldConfig } from '../typings/shared';

type HandleType = 'min' | 'max';

export class SliderField {
	private static valueFiltersRegistered = new Set<string>();

	config: SliderFieldConfig;

	// DOM Elements (initialized / set in setElements(), called from constructor)
	private container!: HTMLElement;
	private slider!: HTMLElement;
	private track!: HTMLElement;
	private fill!: HTMLElement;
	private handlesWrapper!: HTMLElement;
	private handles!: { min: HTMLElement; max: HTMLElement | null };
	private inputs!: { min: HTMLInputElement; max: HTMLInputElement | null };
	private tooltips!: { min: HTMLElement | null; max: HTMLElement | null };
	private valueDisplay!: HTMLElement;

	// State
	private values: { min: number; max: number };
	private activeHandle: HandleType | null = null;

	constructor(config: SliderFieldConfig, container: HTMLElement) {
		this.config = config;

		// Initialize values from config defaults
		this.values = {
			min: config.min,
			max: config.selectionType === 'range' ? config.max : config.min,
		};

		// 1. Find DOM elements
		this.setElements(container);

		// 2. Read initial values from inputs
		this.readValuesFromInputs();

		// 3. Update visual state
		this.updateHandlePosition('min');
		if (this.config.selectionType === 'range') {
			this.updateHandlePosition('max');
		}
		this.updateFill();
		this.updateValueDisplay();

		// 4. Bind event listeners
		this.bindEvents();

		// 5. Support field-level merge tags in GF calculations. The optional
		//    :min/:max modifiers (e.g. {Slider:1:max}) resolve to the field's
		//    configured bounds; with no modifier the selected value is used.
		this.registerCalculationMergeTagSupport();
	}

	// =========================================================================
	// DOM Element Discovery
	// =========================================================================

	private setElements(container: HTMLElement): void {
		this.container = container;

		// Find slider elements
		this.slider = this.container.querySelector(
			'.gps-slider'
		) as HTMLElement;
		this.track = this.container.querySelector(
			'.gps-slider__track'
		) as HTMLElement;
		this.fill = this.container.querySelector(
			'.gps-slider__fill'
		) as HTMLElement;
		this.handlesWrapper = this.container.querySelector(
			'.gps-slider__handles'
		) as HTMLElement;

		// Find handles
		if (this.config.selectionType === 'range') {
			this.handles = {
				min: this.container.querySelector(
					'.gps-slider__handle--min'
				) as HTMLElement,
				max: this.container.querySelector(
					'.gps-slider__handle--max'
				) as HTMLElement,
			};
		} else {
			this.handles = {
				min: this.container.querySelector(
					'.gps-slider__handle'
				) as HTMLElement,
				max: null,
			};
		}

		// Find inputs (.min always present, .max only for range mode)
		this.inputs = {
			min: this.container.querySelector(
				'.gps-slider__input--min'
			) as HTMLInputElement,
			max:
				this.config.selectionType === 'range'
					? (this.container.querySelector(
							'.gps-slider__input--max'
					  ) as HTMLInputElement)
					: null,
		};

		// Find value display
		this.valueDisplay = this.container.querySelector(
			'.gps-slider__value-display'
		) as HTMLElement;

		// Find tooltips (may not exist if showTooltip is false or in form editor)
		this.tooltips = {
			min: this.handles.min?.querySelector(
				'.gps-slider__tooltip'
			) as HTMLElement | null,
			max: this.handles.max?.querySelector(
				'.gps-slider__tooltip'
			) as HTMLElement | null,
		};
	}

	private readValuesFromInputs(): void {
		const minValue = parseFloat(this.inputs.min.value);

		if (!isNaN(minValue)) {
			this.values.min = minValue;
		}

		if (this.config.selectionType === 'range' && this.inputs.max) {
			const maxValue = parseFloat(this.inputs.max.value);
			if (!isNaN(maxValue)) {
				this.values.max = maxValue;
			}
		}
	}

	// =========================================================================
	// Value / Percent Conversion (using snaps from PHP)
	// =========================================================================

	/**
	 * Converts a value to its percentage position using pre-calculated snap positions.
	 * This ensures handle positions exactly match tick mark positions.
	 * @param value
	 */
	private valueToPercent(value: number): number {
		const snap = this.config.snaps.find((s) => s.value === value);
		return snap?.percent ?? 0;
	}

	/**
	 * Converts a percentage to the nearest snap position value.
	 * Finds the closest snap position and returns its value.
	 * @param percent
	 */
	private percentToValue(percent: number): number {
		const snaps = this.config.snaps;
		if (snaps.length === 0) {
			return this.config.min;
		}

		// Find closest snap position by percent
		let closest = snaps[0];
		let closestDist = Math.abs(percent - closest.percent);

		for (const snap of snaps) {
			const dist = Math.abs(percent - snap.percent);
			if (dist < closestDist) {
				closest = snap;
				closestDist = dist;
			}
		}

		return closest.value;
	}

	// =========================================================================
	// Clamping
	// =========================================================================

	/**
	 * Clamps a value to valid bounds.
	 * Uses snaps for min/max bounds, and for range sliders
	 * ensures handles don't cross each other.
	 * @param value
	 * @param handle
	 */
	private clampValue(value: number, handle: HandleType): number {
		const snaps = this.config.snaps;
		if (snaps.length === 0) {
			return value;
		}

		// Get bounds from snap positions (first and last positions)
		let minBound = snaps[0].value;
		let maxBound = snaps[snaps.length - 1].value;

		// For range sliders, constrain handles so they don't cross
		if (this.config.selectionType === 'range') {
			if (handle === 'min') {
				maxBound = this.values.max;
			} else {
				minBound = this.values.min;
			}
		}

		return Math.min(Math.max(value, minBound), maxBound);
	}

	// =========================================================================
	// Event Handling
	// =========================================================================

	private bindEvents(): void {
		// Bind pointer events to handles
		this.handles.min.addEventListener('pointerdown', this.onPointerDown);
		if (this.handles.max) {
			this.handles.max.addEventListener(
				'pointerdown',
				this.onPointerDown
			);
		}

		// Bind keyboard events for accessibility
		this.handles.min.addEventListener('keydown', this.onKeyDown);
		if (this.handles.max) {
			this.handles.max.addEventListener('keydown', this.onKeyDown);
		}

		// Track click to jump to position
		this.track.addEventListener('pointerdown', this.onTrackClick);
	}

	private registerCalculationMergeTagSupport(): void {
		const { formId, fieldId } = this.config;
		const { gform } = window;

		if (
			this.config.selectionType === 'range' ||
			!this.inputs?.min ||
			!gform?.addFilter
		) {
			return;
		}

		const minInputName = this.inputs.min.getAttribute('name') || '';
		if (!minInputName.includes(`input_${fieldId}.`)) {
			return;
		}

		const { min, max } = this.config;

		const filterKey = `${formId}_${fieldId}`;
		if (!SliderField.valueFiltersRegistered.has(filterKey)) {
			SliderField.valueFiltersRegistered.add(filterKey);

			gform.addFilter(
				`gform_value_merge_tag_${formId}_${fieldId}`,
				(value: unknown, _input: unknown, modifier?: string) => {
					// `:min`/`:max` resolve to the field's configured bounds;
					// no modifier resolves to the selected value.
					if (modifier === 'min') {
						return min;
					}
					if (modifier === 'max') {
						return max;
					}

					const selectedInput = document.getElementById(
						`input_${formId}_${fieldId}_1`
					) as HTMLInputElement | null;

					return selectedInput?.classList.contains(
						'gps-slider__input--min'
					)
						? selectedInput.value
						: value;
				}
			);
		}
	}

	private runGravityFormsCalculations(): void {
		const calcObj = window.gf_global?.gfcalc?.[this.config.formId];

		calcObj?.runCalcs?.(this.config.formId, calcObj.formulaFields);
	}

	private onPointerDown = (event: PointerEvent): void => {
		event.preventDefault();
		const target = event.target as HTMLElement;

		// Determine which handle was clicked
		if (target === this.handles.min) {
			this.activeHandle = 'min';
		} else if (target === this.handles.max) {
			this.activeHandle = 'max';
		} else {
			return;
		}

		target.focus({ preventScroll: true });

		// Add dragging class to disable transitions during drag
		this.slider.classList.add('gps-slider--dragging');

		// Add active class to the handle being dragged (for tooltip visibility)
		const handle =
			this.activeHandle === 'min' ? this.handles.min : this.handles.max;
		handle?.classList.add('gps-slider__handle--active');

		// Capture pointer for reliable tracking
		target.setPointerCapture(event.pointerId);

		// Bind move and up handlers
		document.addEventListener('pointermove', this.onPointerMove);
		document.addEventListener('pointerup', this.onPointerUp);
	};

	private onPointerMove = (event: PointerEvent): void => {
		if (!this.activeHandle) {
			return;
		}

		const percent = this.getPercentFromEvent(event);
		let value = this.percentToValue(percent); // Already snapped to nearest position
		value = this.clampValue(value, this.activeHandle);

		this.setValue(this.activeHandle, value);
	};

	private onPointerUp = (event: PointerEvent): void => {
		if (this.activeHandle) {
			const handle =
				this.activeHandle === 'min'
					? this.handles.min
					: this.handles.max;
			handle?.releasePointerCapture(event.pointerId);
			// Remove active class from the handle
			handle?.classList.remove('gps-slider__handle--active');
		}

		// Remove dragging class to re-enable transitions
		this.slider.classList.remove('gps-slider--dragging');

		this.activeHandle = null;
		document.removeEventListener('pointermove', this.onPointerMove);
		document.removeEventListener('pointerup', this.onPointerUp);
	};

	private onTrackClick = (event: PointerEvent): void => {
		// Don't interfere with handle drags
		if (this.activeHandle) {
			return;
		}
		if (
			(event.target as HTMLElement).classList.contains(
				'gps-slider__handle'
			)
		) {
			return;
		}

		const percent = this.getPercentFromEvent(event);
		let value = this.percentToValue(percent); // Already snapped to nearest position

		// Determine which handle to move
		const handle = this.getClosestHandle(value);
		value = this.clampValue(value, handle);
		this.setValue(handle, value);
		this.focusHandle(handle);
	};

	private focusHandle(handle: HandleType): void {
		const element = handle === 'min' ? this.handles.min : this.handles.max;
		element?.focus({ preventScroll: true });
	}

	private getClosestHandle(value: number): HandleType {
		if (this.config.selectionType !== 'range') {
			return 'min';
		}

		const distToMin = Math.abs(value - this.values.min);
		const distToMax = Math.abs(value - this.values.max);

		return distToMin <= distToMax ? 'min' : 'max';
	}

	private getPercentFromEvent(event: PointerEvent): number {
		// Use handles wrapper bounds (compressed range that aligns with tick positions)
		const rect = this.handlesWrapper.getBoundingClientRect();

		if (this.config.orientation === 'vertical') {
			// Vertical: bottom = min (0%), top = max (100%)
			const y = event.clientY - rect.top;
			const percent = 100 - (y / rect.height) * 100;
			return Math.min(Math.max(percent, 0), 100);
		}

		// Horizontal: left = min (0%), right = max (100%)
		const x = event.clientX - rect.left;
		const percent = (x / rect.width) * 100;
		return Math.min(Math.max(percent, 0), 100);
	}

	/**
	 * Handles keyboard navigation for accessibility.
	 * Supports Arrow keys for single-step movement, Home/End for min/max.
	 * @param event
	 */
	private onKeyDown = (event: KeyboardEvent): void => {
		const target = event.target as HTMLElement;
		const handle: HandleType = target === this.handles.min ? 'min' : 'max';

		const currentIndex = this.getSnapIndex(this.values[handle]);
		let newIndex = currentIndex;

		switch (event.key) {
			case 'ArrowRight':
			case 'ArrowUp':
				newIndex = currentIndex + 1;
				break;
			case 'ArrowLeft':
			case 'ArrowDown':
				newIndex = currentIndex - 1;
				break;
			case 'Home':
				newIndex = 0;
				break;
			case 'End':
				newIndex = this.config.snaps.length - 1;
				break;
			default:
				// Don't prevent default for other keys (e.g., Tab)
				return;
		}

		event.preventDefault();

		// Clamp index to valid range
		newIndex = Math.max(
			0,
			Math.min(newIndex, this.config.snaps.length - 1)
		);

		let newValue = this.config.snaps[newIndex].value;
		newValue = this.clampValue(newValue, handle);

		this.setValue(handle, newValue);
	};

	/**
	 * Gets the index of the current value in the snaps array.
	 * @param value
	 */
	private getSnapIndex(value: number): number {
		const index = this.config.snaps.findIndex((s) => s.value === value);
		return index >= 0 ? index : 0;
	}

	// =========================================================================
	// Value Management
	// =========================================================================

	getValue(handle: HandleType): number {
		return this.values[handle];
	}

	setValue(handle: HandleType, value: number): void {
		this.values[handle] = value;
		this.updateHandlePosition(handle);
		this.updateFill();
		this.updateInput(handle);
		this.updateValueDisplay();
		this.updateTooltip(handle);
		this.updateAriaAttributes(handle);
	}

	// =========================================================================
	// DOM Updates
	// =========================================================================

	private updateHandlePosition(handle: HandleType): void {
		const element = handle === 'min' ? this.handles.min : this.handles.max;
		if (!element) {
			return;
		}

		const percent = this.valueToPercent(this.values[handle]);

		if (this.config.orientation === 'vertical') {
			// Vertical: 0% = bottom, 100% = top
			element.style.top = `${100 - percent}%`;
			element.style.left = '50%';
		} else {
			element.style.left = `${percent}%`;
		}
	}

	private updateFill(): void {
		const minPercent = this.valueToPercent(this.values.min);

		if (this.config.selectionType === 'range') {
			const maxPercent = this.valueToPercent(this.values.max);

			if (this.config.orientation === 'vertical') {
				this.fill.style.bottom = `${minPercent}%`;
				this.fill.style.height = `${maxPercent - minPercent}%`;
				this.fill.style.top = 'auto';
			} else {
				this.fill.style.left = `${minPercent}%`;
				this.fill.style.width = `${maxPercent - minPercent}%`;
			}
		} else if (this.config.orientation === 'vertical') {
			// Single vertical: fill from 0 to value
			this.fill.style.bottom = '0';
			this.fill.style.height = `${minPercent}%`;
			this.fill.style.top = 'auto';
		} else {
			// Single horizontal: fill from 0 to value
			this.fill.style.left = '0';
			this.fill.style.width = `${minPercent}%`;
		}
	}

	private totalPriceRecalcScheduled = false;

	private scheduleTotalPriceRecalculation(): void {
		// Coalesce multiple rapid updates (e.g., during drags) into a single total price recalculation.
		if (this.totalPriceRecalcScheduled) {
			return;
		}
		this.totalPriceRecalcScheduled = true;
		const runRecalculation = () => {
			this.totalPriceRecalcScheduled = false;
			if (typeof window.gformCalculateTotalPrice === 'function') {
				window.gformCalculateTotalPrice(this.config.formId);
			}
		};
		if (typeof window.requestAnimationFrame === 'function') {
			window.requestAnimationFrame(runRecalculation);
		} else {
			window.setTimeout(runRecalculation, 0);
		}
	}

	private updateInput(handle: HandleType): void {
		const input = handle === 'min' ? this.inputs.min : this.inputs.max;

		// Guard for single mode (no max input)
		if (!input) {
			return;
		}

		const value = this.values[handle];

		// Format value with configured decimal separator to match GF's numberFormat validation
		input.value = this.formatValue(value);

		// Dispatch input event for Gravity Forms integration
		input.dispatchEvent(new Event('input', { bubbles: true }));
		input.dispatchEvent(new Event('change', { bubbles: true }));

		if (handle === 'min' && this.config.selectionType !== 'range') {
			this.runGravityFormsCalculations();
		}

		// Trigger product total recalculation for Quantity fields mapped to products
		this.scheduleTotalPriceRecalculation();
	}

	private updateValueDisplay(): void {
		if (!this.config.showValue || !this.valueDisplay) {
			return;
		}

		const { prefix, suffix, rangeSeparator } = this.config;

		if (this.config.selectionType === 'range') {
			const minDisplay = this.getDisplayValue(this.values.min);
			const maxDisplay = this.getDisplayValue(this.values.max);
			this.valueDisplay.textContent = `${prefix}${minDisplay}${suffix} ${rangeSeparator} ${prefix}${maxDisplay}${suffix}`;
		} else {
			const display = this.getDisplayValue(this.values.min);
			this.valueDisplay.textContent = `${prefix}${display}${suffix}`;
		}
	}

	private updateTooltip(handle: HandleType): void {
		const tooltip =
			handle === 'min' ? this.tooltips.min : this.tooltips.max;
		if (!tooltip) {
			return;
		}

		const { prefix, suffix } = this.config;
		const display = this.getDisplayValue(this.values[handle]);
		tooltip.textContent = `${prefix}${display}${suffix}`;
	}

	/**
	 * Updates ARIA attributes on the handle for screen reader announcements.
	 * Called after value changes to keep aria-valuenow and aria-valuetext in sync.
	 * @param handle
	 */
	private updateAriaAttributes(handle: HandleType): void {
		const element = handle === 'min' ? this.handles.min : this.handles.max;
		if (!element) {
			return;
		}

		const value = this.values[handle];
		const { prefix, suffix } = this.config;
		const displayValue = this.getDisplayValue(value);
		const valueText = `${prefix}${displayValue}${suffix}`;

		element.setAttribute('aria-valuenow', String(value));
		element.setAttribute('aria-valuetext', valueText);
	}

	/**
	 * Formats a value using the configured decimal separator.
	 * Used for both hidden input values and display values to match GF's numberFormat validation.
	 * @param value
	 */
	private formatValue(value: number): string {
		const formatted = value.toFixed(this.config.decimals);
		if (this.config.decimalSeparator !== '.') {
			return formatted.replace('.', this.config.decimalSeparator);
		}
		return formatted;
	}

	private getDisplayValue(value: number): string {
		if (this.config.mode === 'choices') {
			const choice = this.config.choices.find((c) => c.value === value);
			return choice?.text ?? this.formatValue(value);
		}
		return this.formatValue(value);
	}
}

export function createSliderField(config: SliderFieldConfig) {
	const { formId, fieldId } = config;
	const container = document.getElementById(`field_${formId}_${fieldId}`);

	// The container may exist without slider markup (e.g. the read-only entry detail view).
	if (!container || !container.querySelector('.gps-slider__input--min')) {
		return null;
	}

	return new SliderField(config, container);
}
